import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import EditFaqModal from "../Modals/EditFaqsModal";
import DeleteFaqModal from "../Modals/DeleteFaqModal";
import PublishFaqsModal from "../Modals/PublishFaqModal";
import FaqsCard from "./FaqsCard";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { notifyError, notifySuccess } from "../../utils/helper";
import { getAllFaqs, toggleFaqStatus, updateFaq, deleteFaq } from "../../requests";

const Faqs = ({ dataFetch }) => {
    const [faqData, setFaqData] = useState([]);
    const [openIndex, setOpenIndex] = useState(null);
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [showPublishedDropdown, setShowPublishedDropdown] = useState(false);
    const [menuOpenIndex, setMenuOpenIndex] = useState(null);
    const [menuOpenIndexSecond, setMenuOpenIndexSecond] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentFaq, setCurrentFaq] = useState(null);
    const [editForm, setEditForm] = useState({ question: "", answer: "" });
    const [filters, setFilters] = useState({ status: "All", sortBy: "All" });
    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();
    const sortDropdownRef = useRef(null);
    const publishedDropdownRef = useRef(null);

    // Fetch FAQs from backend
    const fetchFaqs = async () => {
        try {
            dispatch(startLoader());
            setIsLoading(true);
            const response = await getAllFaqs();
            setFaqData(response.data || []);
        } catch (error) {
            console.error("Error fetching FAQs:", error);
            notifyError("Failed to fetch FAQs");
        } finally {
            dispatch(stopLoader());
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFaqs();
    }, [dispatch]);

    useEffect(() => {
        if (dataFetch) {
            fetchFaqs();
        }
    }, [dataFetch]);

    // Filter and sort logic
    const isThisWeek = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
        const endOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 6));
        return date >= startOfWeek && date <= endOfWeek;
    };

    const isThisMonth = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    };

    const isThisYear = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        return date.getFullYear() === now.getFullYear();
    };

    // Filter and sort logic
    const filteredFaqs = faqData
        .filter((faq) => {
            if (filters.status === "All") return true;
            if (filters.status === "Publish") return faq.status === "Publish";
            if (filters.status === "Draft") return faq.status === "Draft";
            return true;
        })
        .filter((faq) => {
            if (filters.sortBy === "All") return true;
            if (filters.sortBy === "This Week") return isThisWeek(faq.created_at);
            if (filters.sortBy === "This Month") return isThisMonth(faq.created_at);
            if (filters.sortBy === "This Year") return isThisYear(faq.created_at);
            return true;
        })

        .sort((a, b) => {
            // Always sort by date descending (newest first) for date filters
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA; // Newest first
        });

    const leftColumnFaqs = filteredFaqs.filter((_, idx) => idx % 2 === 0);
    const rightColumnFaqs = filteredFaqs.filter((_, idx) => idx % 2 !== 0);

    // Toggle logic
    const toggleFAQ = (index) => setOpenIndex(openIndex === index ? null : index);

    const toggleMenu = (index, e) => {
        e.stopPropagation();
        setMenuOpenIndex(menuOpenIndex === index ? null : index);
        setMenuOpenIndexSecond(null);
    };

    const toggleMenuSecond = (index, e) => {
        e.stopPropagation();
        setMenuOpenIndexSecond(menuOpenIndexSecond === index ? null : index);
        setMenuOpenIndex(null);
    };

    const handleMenuAction = (action, faq, column, index, e) => {
        e.stopPropagation();
        setCurrentFaq(faq);

        if (action === "Edit") {
            setEditForm({ question: faq.question, answer: faq.answer });
            setShowEditModal(true);
        } else if (action === "Publish" || action === "Unpublish") {
            setShowPublishModal(true);
        } else if (action === "Delete") {
            setShowDeleteModal(true);
        }

        setMenuOpenIndex(null);
        setMenuOpenIndexSecond(null);
    };

    // Confirm publish/unpublish
    const handlePublishConfirm = async () => {
        if (!currentFaq) return;

        try {
            dispatch(startLoader());
            setIsLoading(true);

            await toggleFaqStatus(currentFaq.id);

            // Refetch FAQs to get updated data from server
            await fetchFaqs();

            setShowPublishModal(false);
            setCurrentFaq(null);
            notifySuccess(
                `FAQ ${currentFaq.status === "Publish" ? "unpublished" : "published"} successfully`
            );
        } catch (error) {
            console.error("Error toggling FAQ status:", error);
            notifyError("Failed to update FAQ status");
        } finally {
            dispatch(stopLoader());
            setIsLoading(false);
        }
    };

    // Save edit
    const handleEditSave = async () => {
        if (!currentFaq || !editForm.question.trim() || !editForm.answer.trim()) {
            notifyError("Please fill in both question and answer");
            return;
        }

        try {
            dispatch(startLoader());
            setIsLoading(true);

            await updateFaq(currentFaq.id, {
                question: editForm.question.trim(),
                answer: editForm.answer.trim()
            });

            // Refetch FAQs to get updated data from server
            await fetchFaqs();

            setShowEditModal(false);
            setCurrentFaq(null);
            setEditForm({ question: "", answer: "" });
            notifySuccess("FAQ updated successfully");
        } catch (error) {
            console.error("Error updating FAQ:", error);
            notifyError("Failed to update FAQ");
        } finally {
            dispatch(stopLoader());
            setIsLoading(false);
        }
    };

    // Confirm delete
    const handleDeleteConfirm = async () => {
        if (!currentFaq) return;

        try {
            dispatch(startLoader());
            setIsLoading(true);

            await deleteFaq(currentFaq.id);

            // Refetch FAQs to get updated data from server
            await fetchFaqs();

            setShowDeleteModal(false);
            setCurrentFaq(null);
            notifySuccess("FAQ deleted successfully");
        } catch (error) {
            console.error("Error deleting FAQ:", error);
            notifyError("Failed to delete FAQ");
        } finally {
            dispatch(stopLoader());
            setIsLoading(false);
        }
    };

    // Dropdown toggles
    const togglePublishedDropdown = () => {
        setShowPublishedDropdown(!showPublishedDropdown);
        setShowSortDropdown(false);
    };

    const toggleSortDropdown = () => {
        setShowSortDropdown(!showSortDropdown);
        setShowPublishedDropdown(false);
    };

    // Handle dropdown selection
    const handleSortSelect = (sortOption) => {
        setFilters((prev) => ({ ...prev, sortBy: sortOption }));
        setShowSortDropdown(false);
    };

    const handleStatusSelect = (statusOption) => {
        setFilters((prev) => ({ ...prev, status: statusOption }));
        setShowPublishedDropdown(false);
    };

    const getMenuOptions = (status) =>
        status === "Publish" ? ["Unpublish", "Edit", "Delete"] : ["Publish", "Edit", "Delete"];

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            // Check if we're clicking inside a menu trigger or dropdown
            const menuTrigger = e.target.closest('[data-menu-trigger]');
            const dropdownMenu = e.target.closest('[data-dropdown-menu]');

            if (!menuTrigger && !dropdownMenu) {
                setMenuOpenIndex(null);
                setMenuOpenIndexSecond(null);
            }

            if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
                setShowSortDropdown(false);
            }
            if (publishedDropdownRef.current && !publishedDropdownRef.current.contains(e.target)) {
                setShowPublishedDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Loading state
    if (isLoading && faqData.length === 0) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px"
            }}>
                <div>Loading FAQs...</div>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/* Filters */}
            <div style={{ display: "flex", gap: "8px" }}>
                {/* Sort Dropdown */}
                <div style={{ position: "relative" }} ref={sortDropdownRef}>
                    <button
                        onClick={toggleSortDropdown}
                        style={filterButtonStyle}
                        disabled={isLoading}
                    >
                        {filters.sortBy} <ChevronDown size={16} color="#6b7280" />
                    </button>

                    {showSortDropdown && (
                        <Dropdown
                            items={["All", "This Week", "This Month", "This Year"]}
                            onSelect={handleSortSelect}
                        />
                    )}
                </div>

                {/* Status Dropdown */}
                <div style={{ position: "relative" }} ref={publishedDropdownRef}>
                    <button
                        onClick={togglePublishedDropdown}
                        style={filterButtonStyle}
                        disabled={isLoading}
                    >
                        {filters.status} <ChevronDown size={16} color="#6b7280" />
                    </button>

                    {showPublishedDropdown && (
                        <Dropdown
                            items={["All", "Publish", "Draft"]}
                            onSelect={handleStatusSelect}
                        />
                    )}
                </div>
            </div>

            {/* FAQ Cards Grid */}
            {filteredFaqs.length === 0 ? (
                <div style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6b7280",
                    fontFamily: "Plus Jakarta Sans"
                }}>
                    {faqData.length === 0 ? "No FAQs found." : "No FAQs match the current filters."}
                </div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                    <div>
                        {leftColumnFaqs.map((faq, index) => (
                            <FaqsCard
                                key={faq.id}
                                faq={faq}
                                index={index}
                                actualIndex={index * 2}
                                isLeftColumn
                                openIndex={openIndex}
                                menuOpenIndex={menuOpenIndex}
                                menuOpenIndexSecond={menuOpenIndexSecond}
                                toggleFAQ={toggleFAQ}
                                toggleMenu={toggleMenu}
                                toggleMenuSecond={toggleMenuSecond}
                                handleMenuAction={handleMenuAction}
                                getMenuOptions={getMenuOptions}
                                isLoading={isLoading}
                            />
                        ))}
                    </div>

                    <div>
                        {rightColumnFaqs.map((faq, index) => (
                            <FaqsCard
                                key={faq.id}
                                faq={faq}
                                index={index}
                                actualIndex={index * 2 + 1}
                                openIndex={openIndex}
                                menuOpenIndex={menuOpenIndex}
                                menuOpenIndexSecond={menuOpenIndexSecond}
                                toggleFAQ={toggleFAQ}
                                toggleMenu={toggleMenu}
                                toggleMenuSecond={toggleMenuSecond}
                                handleMenuAction={handleMenuAction}
                                getMenuOptions={getMenuOptions}
                                isLoading={isLoading}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Modals */}
            <EditFaqModal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                editForm={editForm}
                setEditForm={setEditForm}
                onSave={handleEditSave}
                isLoading={isLoading}
            />

            <PublishFaqsModal
                show={showPublishModal}
                onHide={() => setShowPublishModal(false)}
                onConfirm={handlePublishConfirm}
                currentFaq={currentFaq}
                isLoading={isLoading}
            />

            <DeleteFaqModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                isLoading={isLoading}
            />
        </div>
    );
};

export default Faqs;

const filterButtonStyle = {
    fontFamily: "Plus Jakarta Sans",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "20px",
    borderRadius: "42px",
    border: "1px solid #D1D5DB",
    cursor: "pointer",
    color: "#6b7280",
    background: "white",
    transition: "all 0.2s ease-in-out",
};

//dropdown component
const Dropdown = ({ items, onSelect }) => (
    <div
        style={{
            width: "max-content",
            position: "absolute",
            top: "110%",
            left: 0,
            backgroundColor: "white",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
            padding: "4px 0",
            zIndex: 999,
            border: "1px solid #E5E7EB",
        }}
    >
        {items.map((item, idx) => (
            <div
                key={idx}
                onClick={() => onSelect(item)}
                style={{
                    fontWeight: 400,
                    padding: "10px 16px 10px 12px",
                    fontSize: "14px",
                    fontFamily: "Plus Jakarta Sans",
                    color: "#6B7280",
                    cursor: "pointer",
                    borderLeft: "4px solid transparent",
                    transition: "all 0.2s ease-in-out",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#F0FDFA";
                    e.currentTarget.style.borderLeft = "4px solid #06C270";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.borderLeft = "4px solid transparent";
                }}
            >
                {item}
            </div>
        ))}
    </div>
);